import { createFileRoute, notFound, useLoaderData } from '@tanstack/react-router'
import { AssetNotFound } from '../../components/errors/NotFound.tsx'
import { assetQuery, useFavoriteAssets } from '../../data/assets.ts'
import { assetAsLocation } from '../../data/locations.tsx'
import { lightningProtectionsQuery } from '../../data/protections/lightning.ts'
import { protectionsQuery, toProtections } from '../../data/protections/protections.ts'
import { Overview } from '../../features/impacts/components/Overview.tsx'
import { AssetTimezoneProvider } from '../../features/impacts/timezone.tsx'
import { useImpactsDashboard } from '../../states.ts'

export const Route = createFileRoute('/_app/impacts/$id')({
	component: RouteComponent,
	notFoundComponent: () => <AssetNotFound />,
	beforeLoad: async ({ context: { queryClient, impersonation, sub } }) => {
		const hasProtectionsSub = sub.features.includes('protections')
		const [assets, protections, lightningProtections] = await Promise.all([
			queryClient.ensureQueryData(assetQuery(impersonation)),
			queryClient.ensureQueryData(protectionsQuery(impersonation, { enabled: hasProtectionsSub })),
			queryClient.ensureQueryData(lightningProtectionsQuery(impersonation, { enabled: hasProtectionsSub })),
		])
		const protectedVrns = new Set([...toProtections(protections), ...lightningProtections].map((p) => p.assetVrn))
		return { assets: assets.map(assetAsLocation).filter((a) => protectedVrns.has(a.assetVrn)) }
	},
	loader: ({ context: { assets }, params: { id } }) => {
		const asset = assets.find((a) => a.id === id)
		if (!asset) throw notFound()
		return { asset, allAssets: assets }
	},
})

function RouteComponent() {
	const { asset, allAssets } = useLoaderData({ from: '/_app/impacts/$id' })
	const { data: favorites = [] } = useFavoriteAssets()
	const { period } = useImpactsDashboard()
	const favoriteVrns = new Set(favorites.map((f) => f.assetVrn))
	const sortedAssets = [...allAssets].sort(
		(a, b) => Number(favoriteVrns.has(b.assetVrn)) - Number(favoriteVrns.has(a.assetVrn)) || a.name.localeCompare(b.name),
	)

	return (
		<AssetTimezoneProvider key={asset.id} asset={asset}>
			<Overview asset={asset} allAssets={sortedAssets} period={period} />
		</AssetTimezoneProvider>
	)
}
